/* eslint-disable camelcase */
import React, { useContext, useState } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import { PNCounterIcon } from '@mummssoftware/global-context/PNCounter';
import { ENTITY_PATIENT_CARE_PLAN_PROBLEM_TASK } from '@mummssoftware/global-context/PNCounter/constants';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import { formatPimDate } from '@mummssoftware/utils/formatters';
import { VisitContext } from '../Context/contexts/VisitContext';
import {
  CellDiv,
  CellCountDiv,
  StyledSpan,
} from '../../components/layout/VisitWrapper';
import {
  Completed,
  Refused,
  MINIMIZE_VISIT_DIALOG,
  ADD_TASK,
  DELETE_TASK,
} from '../Context/constants';
import { Context } from '../../context';
import { isTaskCompleted, tasksCompleted } from './utils';
import { useStyles } from './style';

export const FrequencyCellRenderer = React.memo((props: any) => {
  const { data } = props && props;
  const text = `${data.frequency} x ${data.period}`;

  return (
    <CellDiv>
      <StyledSpan>{text}</StyledSpan>
    </CellDiv>
  );
});

export const DoneActionCellRenderer = React.memo((props: any) => {
  const classes = useStyles();
  const { data } = props && props;
  const { appVisit } = useContext(GlobalContext);
  const { tasksToBeSaved, tasksToBeDeleted, dispatchVisit } = useContext(
    VisitContext,
  );

  const { id, effectivedatetime } = appVisit as mumms.Visit;

  // if Completed Task checked on the other column
  const isVisitDayTaskChecked =
    tasksToBeSaved &&
    tasksToBeSaved[data.id] &&
    tasksToBeSaved[data.id].status === Refused;

  const isVisitDayTaskDeleted =
    tasksToBeDeleted &&
    tasksToBeDeleted[data.id] &&
    tasksToBeDeleted[data.id].status === Refused;
  // isTaskDone is task object if any
  const taskDone = isTaskCompleted(
    data,
    effectivedatetime,
    Completed,
    appVisit?.id,
  );
  const taskRefused = isTaskCompleted(
    data,
    effectivedatetime,
    Refused,
    appVisit?.id,
  );

  const taskCantCheck = taskRefused && !isVisitDayTaskDeleted;

  const { currentUser } = useContext(Context);

  // if Visit is in Progress then show the tasks executed as checked
  // if new Visit leave it blank
  const isTaskAlreadyChecked = !!taskDone;
  const [taskChecked, setTaskChecked] = useState<boolean>(isTaskAlreadyChecked);

  const task = {
    actionbypersoncredentials: currentUser.userCredentials,
    actionbyperson_id: currentUser.userId.toString(),
    actiondatetime: formatPimDate(Date.now()),
    // @note effectivedatetime is in UTC time
    patientvisitdatetime: formatPimDate(effectivedatetime),
    actiontaken: Completed,
    id: '',
    iscompleted: 'true',
    patientcpptask_id: data.id,
    patientvisit_id: id.toString(),
    status: Completed,
  };

  /**
   * if no prior task done follow ag grid flow
   */
  const onClick = (event: any) => {
    const { checked } = event.target;
    const { colId } = props.column;
    props.node.setDataValue(colId, checked);
    dispatchVisit({
      type: ADD_TASK,
      payload: {
        id: data.id,
        task,
      },
    });
  };

  /**
   * if isTaskDone workflow
   */
  const onTaskDoneClick = (event: any) => {
    const { colId } = props.column;
    props.node.setDataValue(colId, taskChecked);
    if (taskChecked) {
      setTaskChecked(false);
      // to match the deleteTaskMapping in Visit Context
      dispatchVisit({
        type: DELETE_TASK,
        payload: {
          id: taskDone.patientcpptask_id,
          task,
        },
      });
    } else {
      setTaskChecked(true);
      dispatchVisit({
        type: DELETE_TASK,
        payload: {
          id: taskDone.patientcpptask_id,
          task,
        },
      });
    }
  };

  return isTaskAlreadyChecked ? (
    <CellDiv>
      <Checkbox
        checked={taskChecked}
        color="primary"
        onClick={onTaskDoneClick}
        classes={{
          root: classes.root,
          checked: classes.checked,
        }}
        disabled={!!isVisitDayTaskChecked}
      />
    </CellDiv>
  ) : (
    <CellDiv>
      <Checkbox
        checked={!!props.value}
        color="primary"
        onClick={onClick}
        classes={{
          root: classes.root,
          checked: classes.checked,
        }}
        disabled={!!taskCantCheck || !!isVisitDayTaskChecked}
      />
    </CellDiv>
  );
});

export const RefusedActionCellRenderer = React.memo((props: any) => {
  const classes = useStyles();
  const { data } = props && props;
  const { appVisit } = useContext(GlobalContext);
  const { tasksToBeSaved, tasksToBeDeleted, dispatchVisit } = useContext(
    VisitContext,
  );

  const { id, effectivedatetime } = appVisit as mumms.Visit;

  // if Completed Task checked on the other column
  const isVisitDayTaskChecked =
    tasksToBeSaved &&
    tasksToBeSaved[data.id] &&
    tasksToBeSaved[data.id].status === Completed;

  const isVisitDayTaskDeleted =
    tasksToBeDeleted &&
    tasksToBeDeleted[data.id] &&
    tasksToBeDeleted[data.id].status === Completed;

  // isTaskDone is task object if any
  const taskRefused = isTaskCompleted(
    data,
    effectivedatetime,
    Refused,
    appVisit?.id,
  );
  const taskCompleted = isTaskCompleted(
    data,
    effectivedatetime,
    Completed,
    appVisit?.id,
  );

  const taskCantCheck = taskCompleted && !isVisitDayTaskDeleted;

  const { currentUser } = useContext(Context);

  // if Visit is in Progress then show the tasks executed as checked
  // if new Visit leave it blank
  const isTaskAlreadyChecked = !!taskRefused;
  const [taskChecked, setTaskChecked] = useState<boolean>(isTaskAlreadyChecked);

  const task = {
    actionbypersoncredentials: currentUser.userCredentials,
    actionbyperson_id: currentUser.userId.toString(),
    actiondatetime: formatPimDate(Date.now()),
    // @note effectivedatetime is in UTC time
    patientvisitdatetime: formatPimDate(effectivedatetime),
    actiontaken: Refused,
    id: '',
    iscompleted: 'true',
    patientcpptask_id: data.id,
    patientvisit_id: id.toString(),
    status: Refused,
  };

  /**
   * if no prior task done follow ag grid flow
   */
  const onClick = (event: any) => {
    const { checked } = event.target;
    const { colId } = props.column;
    props.node.setDataValue(colId, checked);
    dispatchVisit({
      type: ADD_TASK,
      payload: {
        id: data.id,
        task,
      },
    });
  };

  /**
   * if isTaskDone workflow
   */
  const onTaskRefuseClick = (event: any) => {
    const { colId } = props.column;
    props.node.setDataValue(colId, taskChecked);
    if (taskChecked) {
      setTaskChecked(false);
      // to match the deleteTaskMapping in Visit Context
      dispatchVisit({
        type: DELETE_TASK,
        payload: {
          id: taskRefused.patientcpptask_id,
          task,
        },
      });
    } else {
      setTaskChecked(true);
      dispatchVisit({
        type: DELETE_TASK,
        payload: {
          id: taskRefused.patientcpptask_id,
          task,
        },
      });
    }
  };

  return isTaskAlreadyChecked ? (
    <CellDiv>
      <Checkbox
        checked={taskChecked}
        color="primary"
        onClick={onTaskRefuseClick}
        classes={{
          root: classes.refusedRoot,
          checked: classes.refusedChecked,
        }}
        indeterminate={!!taskChecked}
        disabled={!!isVisitDayTaskChecked}
      />
    </CellDiv>
  ) : (
    <CellDiv>
      <Checkbox
        checked={!!props.value}
        color="primary"
        onClick={onClick}
        classes={{
          root: classes.refusedRoot,
          checked: classes.refusedChecked,
        }}
        disabled={!!taskCantCheck || !!isVisitDayTaskChecked}
      />
    </CellDiv>
  );
});

export const DoneCellRenderer = React.memo((props: any) => {
  const { data, dateColumn } = props && props;

  const tasksDone = tasksCompleted(data, dateColumn, Completed);
  const tasksRefused = tasksCompleted(data, dateColumn, Refused);
  const showTaskDone = tasksDone && tasksDone.length;
  const showTaskRefused = tasksRefused && tasksRefused.length;
  const taskTextC = showTaskDone ? tasksDone.length : '';
  const commaText = showTaskDone ? ',' : '';
  const refusedText =
    showTaskRefused && tasksRefused.length === 1 && !showTaskDone
      ? 'X'
      : `${tasksRefused.length} X`;
  const taskTextR = showTaskRefused ? `${commaText} ${refusedText}` : '';
  const text = `${taskTextC} ${taskTextR}`;

  return showTaskDone || showTaskRefused ? (
    <CellCountDiv id="count-done">{text}</CellCountDiv>
  ) : (
    <></>
  );
});

// do not show note for DC tasks
export const NoteCountCellRenderer = (props: any) => {
  const { dispatchVisit } = useContext(VisitContext);

  const onIconClick = () => {
    dispatchVisit({
      type: MINIMIZE_VISIT_DIALOG,
      payload: true,
    });
  };
  return (
    /* eslint camelcase: ["error", {allow: ["role_group_name"]}] */
    <PNCounterIcon
      patientNumber={props?.data?.patientnumber}
      entityKey={props?.data?.entitykey}
      entityName={props?.data?.tasktext}
      entityType={ENTITY_PATIENT_CARE_PLAN_PROBLEM_TASK}
      onIconClick={onIconClick}
    />
  );
};
